import {
  useHttpClient,
  useNextSortOrder,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentProposalOptionModifier,
  AppointmentProposalOptionModifierCreate,
  AppointmentProposalOptionModifierUpdate,
} from './appointmentProposalOptionModifiers.models';
import {
  initState,
  optionModifierReducer,
} from './appointmentProposalOptionModifiers.reducer';

type CreatePayload = AppointmentProposalOptionModifierCreate;
type UpdatePayload = AppointmentProposalOptionModifierUpdate;

interface OfflineAppointmentProposalOptionModifiersContextProps {
  appendModifiers: (modifiers: AppointmentProposalOptionModifier[]) => void;
  upsertModifiers: (modifiers: AppointmentProposalOptionModifier[]) => void;
  selectProposalModifiers: (appointmentProposalId: string) => void;
  setModifiersMutating: (opts: { creatingIds?: string[] }) => void;
  clearModifiersMutating: (opts: {
    creatingIds?: string[];
    error?: string;
  }) => void;
}

interface AppointmentProposalOptionModifiersContextProps
  extends OfflineAppointmentProposalOptionModifiersContextProps {
  proposalModifiers: AppointmentProposalOptionModifier[];
  isLoading: boolean;
  isLoaded: boolean;
  isLoadingModifierIds: string[];
  error: string | null;
  fetchAppointmentProposalModifiers: (
    appointmentId: string,
    appointmentProposalId: string
  ) => Promise<AppointmentProposalOptionModifier[]>;
  fetchAppointmentProposalOptionModifiers: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string
  ) => Promise<AppointmentProposalOptionModifier[]>;
  createAppointmentProposalOptionModifier: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    data: CreatePayload
  ) => Promise<AppointmentProposalOptionModifier>;
  createManyAppointmentProposalOptionModifiers: (
    appointmentId: string,
    appointmentProposalId: string,
    payloads: {
      appointmentProposalOptionId: string;
      modifier: CreatePayload;
    }[]
  ) => Promise<AppointmentProposalOptionModifier[]>;
  updateAppointmentProposalOptionModifier: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionModifierId: string,
    data: UpdatePayload
  ) => Promise<AppointmentProposalOptionModifier>;
  deleteAppointmentProposalOptionModifier: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionModifierId: string
  ) => Promise<void>;
  getNextSortOrder: (offset?: number) => number;
}

export const AppointmentProposalOptionModifiersContext = createContext<
  AppointmentProposalOptionModifiersContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren;

export const AppointmentProposalOptionModifiersProvider: React.FC<OwnProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(optionModifierReducer, initState);
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const proposalModifiers = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentProposalId === state.selectedProposalId &&
            a.status !== 'DELETED'
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids, state.selectedProposalId]
  );

  const fetchAppointmentProposalModifiers: AppointmentProposalOptionModifiersContextProps['fetchAppointmentProposalModifiers'] =
    useCallback(
      async (appointmentId, appointmentProposalId) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS',
          payload: { appointmentProposalId },
        });
        try {
          const optionModifiers = await httpClient.get<
            AppointmentProposalOptionModifier[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/modifiers`
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_SUCCESS',
            payload: { optionModifiers, appointmentProposalId },
          });
          return optionModifiers;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const fetchAppointmentProposalOptionModifiers: AppointmentProposalOptionModifiersContextProps['fetchAppointmentProposalOptionModifiers'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId
      ) => {
        dispatch({
          type: 'FETCH_OPTION_MODIFIERS',
          payload: { appointmentProposalId },
        });
        try {
          const optionModifiers = await httpClient.get<
            AppointmentProposalOptionModifier[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/modifiers`
          );
          dispatch({
            type: 'FETCH_OPTION_MODIFIERS_SUCCESS',
            payload: { optionModifiers, appointmentProposalId },
          });
          return optionModifiers;
        } catch (error) {
          dispatch({
            type: 'FETCH_OPTION_MODIFIERS_FAILURE',
            payload: { error: `Failed to fetch modifiers: ${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const createAppointmentProposalOptionModifierInternal = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalOptionId: string,
      data: CreatePayload
    ) => {
      return await httpClient.post<AppointmentProposalOptionModifier>(
        `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/modifiers`,
        data
      );
    },
    [httpClient]
  );

  const createAppointmentProposalOptionModifier: AppointmentProposalOptionModifiersContextProps['createAppointmentProposalOptionModifier'] =
    useCallback(
      async (
        appointmentId: string,
        appointmentProposalId: string,
        appointmentProposalOptionId: string,
        data: CreatePayload
      ) => {
        dispatch({
          type: 'CREATE_OPTION_MODIFIER',
          payload: { modifierId: data.discountsAndFeesId },
        });
        try {
          const optionModifier =
            await createAppointmentProposalOptionModifierInternal(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              data
            );
          dispatch({
            type: 'CREATE_OPTION_MODIFIER_SUCCESS',
            payload: { optionModifier },
          });
          return optionModifier;
        } catch (error) {
          dispatch({
            type: 'CREATE_OPTION_MODIFIER_FAILURE',
            payload: {
              error: `Error creating modifier: ${error}`,
              modifierId: data.discountsAndFeesId,
            },
          });
          throw error;
        }
      },
      [createAppointmentProposalOptionModifierInternal]
    );

  const createManyAppointmentProposalOptionModifiers: AppointmentProposalOptionModifiersContextProps['createManyAppointmentProposalOptionModifiers'] =
    useCallback(
      async (appointmentId, appointmentProposalId, payloads) => {
        const modifierIds = payloads.map((p) => p.modifier.discountsAndFeesId);

        dispatch({
          type: 'CREATE_MANY_OPTION_MODIFIERS',
          payload: { modifierIds },
        });

        try {
          const createdModifiers = await Promise.all(
            payloads.map(({ appointmentProposalOptionId, modifier }) =>
              createAppointmentProposalOptionModifierInternal(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                modifier
              )
            )
          );

          dispatch({
            type: 'CREATE_MANY_OPTION_MODIFIERS_SUCCESS',
            payload: { optionModifiers: createdModifiers, modifierIds },
          });
          return createdModifiers;
        } catch (error) {
          dispatch({
            type: 'CREATE_MANY_OPTION_MODIFIERS_FAILURE',
            payload: { error: `${error}`, modifierIds },
          });
          return [];
        }
      },
      [createAppointmentProposalOptionModifierInternal]
    );

  const updateAppointmentProposalOptionModifier: AppointmentProposalOptionModifiersContextProps['updateAppointmentProposalOptionModifier'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionModifierId,
        data
      ) => {
        dispatch({ type: 'UPDATE_OPTION_MODIFIER' });
        try {
          const updated =
            await httpClient.put<AppointmentProposalOptionModifier>(
              `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/modifiers/${appointmentProposalOptionModifierId}`,
              data
            );
          dispatch({
            type: 'UPDATE_OPTION_MODIFIER_SUCCESS',
            payload: { optionModifier: updated },
          });
          return updated;
        } catch (error) {
          dispatch({
            type: 'UPDATE_OPTION_MODIFIER_FAILURE',
            payload: { error: `Error updating modifier: ${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const deleteAppointmentProposalOptionModifier: AppointmentProposalOptionModifiersContextProps['deleteAppointmentProposalOptionModifier'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionModifierId
      ) => {
        dispatch({ type: 'DELETE_OPTION_MODIFIER' });
        try {
          await httpClient.delete(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/modifiers/${appointmentProposalOptionModifierId}`
          );
          dispatch({
            type: 'DELETE_OPTION_MODIFIER_SUCCESS',
            payload: { appointmentProposalOptionModifierId },
          });
        } catch (error) {
          dispatch({
            type: 'DELETE_OPTION_MODIFIER_FAILURE',
            payload: { error: `Error deleting modifier: ${error}` },
          });
        }
      },
      [httpClient]
    );

  const getNextSortOrder = useNextSortOrder(proposalModifiers);

  const appendModifiers: AppointmentProposalOptionModifiersContextProps['appendModifiers'] =
    useCallback((appointmentProposalOptionModifiers) => {
      dispatch({
        type: 'UPSERT_MODIFIERS',
        payload: { appointmentProposalOptionModifiers },
      });
    }, []);

  const selectProposalModifiers: AppointmentProposalOptionModifiersContextProps['selectProposalModifiers'] =
    useCallback((appointmentProposalId: string) => {
      dispatch({
        type: 'SELECT_PROPOSAL_MODIFIERS',
        payload: { appointmentProposalId },
      });
    }, []);

  const setModifiersMutating: AppointmentProposalOptionModifiersContextProps['setModifiersMutating'] =
    useCallback((opts) => {
      dispatch({ type: 'SET_MODIFIERS_MUTATING', payload: opts });
    }, []);

  const clearModifiersMutating: AppointmentProposalOptionModifiersContextProps['clearModifiersMutating'] =
    useCallback((opts) => {
      dispatch({ type: 'CLEAR_MODIFIERS_MUTATING', payload: opts });
    }, []);

  return (
    <AppointmentProposalOptionModifiersContext.Provider
      value={{
        proposalModifiers,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isLoadingModifierIds: state.isLoadingModifierIds,
        error: state.error,
        fetchAppointmentProposalModifiers,
        fetchAppointmentProposalOptionModifiers,
        createAppointmentProposalOptionModifier,
        createManyAppointmentProposalOptionModifiers,
        updateAppointmentProposalOptionModifier,
        deleteAppointmentProposalOptionModifier,
        getNextSortOrder,
        appendModifiers,
        upsertModifiers: appendModifiers,
        selectProposalModifiers,
        setModifiersMutating,
        clearModifiersMutating,
      }}
    >
      {children}
    </AppointmentProposalOptionModifiersContext.Provider>
  );
};

export const useAppointmentProposalOptionModifiers = () => {
  const context = useContext(AppointmentProposalOptionModifiersContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalOptionModifiers must be used within a AppointmentProposalOptionModifiersProvider'
    );
  }
  return context;
};
